import { createFileRoute } from "@tanstack/react-router";
import { useBilling } from "@/features/billing/api/useBilling";
import { useCreateCheckoutSession } from "@/features/billing/api/createCheckoutSession";
import { useCreatePortalSession } from "@/features/billing/api/createPortalSession";
import { Button } from "@patient-watch/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@patient-watch/ui/card";
import { Badge } from "@patient-watch/ui/badge";
import { SubscriptionPlanBadge } from "@patient-watch/ui/subscription-plan-badge";
import { AlertCircle, CreditCard, Loader2 } from "lucide-react";
import { PlanPickerCards } from "@/features/billing/components/PlanPickerCards";
import { Alert, AlertDescription, AlertTitle } from "@patient-watch/ui/alert";
import dayjs from "dayjs";
import { commonComponents as cc } from "@/components/common";
import { hooks as h } from "@/hooks";
import { isAtDiaryCap, useClinicianDiaryUsage } from "@/features/invite/api/useClinicianDiaryUsage";
import { useInviteCopy } from "@/features/i18n/inviteCopy";
import { useProfileCopy } from "@/features/i18n/profileCopy";
import { useAuth } from "@/utils/auth";

const websiteUrl = import.meta.env.VITE_WEBSITE_URL || "https://patient-watch.com";

export const Route = createFileRoute("/_app/profile/billing")({
    component: BillingPage,
});

function BillingPage() {
    const auth = useAuth();
    const profileCopy = useProfileCopy();
    const inviteCopy = useInviteCopy();
    const { data: billing, isLoading, error } = useBilling(auth.id);
    const { data: usage, isLoading: usageLoading } = useClinicianDiaryUsage(auth.id);
    const createCheckout = useCreateCheckoutSession();
    const createPortal = useCreatePortalSession();

    h.useNav([
        { label: profileCopy.title, route: { to: "/profile" } },
        { label: profileCopy.billingAndSubscription, route: { to: "/profile/billing" } },
    ]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-[400px]">
                <Loader2 className="w-8 h-8 text-cyan-600 animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="destructive">
                <AlertCircle size={20} />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{profileCopy.failedToLoadBilling}</AlertDescription>
            </Alert>
        );
    }

    const hasBilling = Boolean(billing);
    const isActive = billing?.subscription_status === "active";
    const isCanceled = billing?.subscription_status === "canceled";
    const isCancelingAtPeriodEnd = Boolean(billing?.cancel_at_period_end) && isActive;
    const needsPayment = billing?.subscription_status === "past_due" || billing?.subscription_status === "unpaid";
    const isTrialPlan = billing?.subscription_plan === "trial";
    const hasStripeCustomer = Boolean(billing?.stripe_customer_id);
    const hasStripeSubscription = Boolean(billing?.stripe_subscription_id);
    const canCancelSubscription = hasStripeCustomer && !isCancelingAtPeriodEnd && !isCanceled;
    const isBusy = createPortal.isPending || createCheckout.isPending;
    const periodEndLabel = billing?.current_period_end ? dayjs(billing.current_period_end).format("DD MMM YYYY") : null;
    const atCap = isAtDiaryCap(usage);
    const usageLabel =
        usage == null
            ? null
            : usage.diary_limit == null
              ? profileCopy.diaryUsageUnlimited
              : inviteCopy.diaryQuotaUsage(usage.used, usage.diary_limit);

    const statusLabel = isCancelingAtPeriodEnd
        ? profileCopy.billingStatusCanceling
        : billing?.subscription_status?.toUpperCase() || profileCopy.billingStatusUnknown;

    const getStatusBadgeVariant = (
        status: string | null | undefined
    ): "default" | "secondary" | "destructive" | "outline" => {
        if (isCancelingAtPeriodEnd) return "secondary";
        if (!status) return "outline";
        switch (status) {
            case "active":
                return "default";
            case "canceled":
                return "secondary";
            case "past_due":
            case "unpaid":
                return "destructive";
            default:
                return "outline";
        }
    };

    return (
        <div className="mx-auto px-4 py-8 max-w-4xl container">
            <cc.Title words={profileCopy.billingAndSubscription} description={profileCopy.description} />

            {needsPayment && (
                <Alert variant="destructive" className="mb-6">
                    <AlertCircle size={20} />
                    <AlertTitle>{profileCopy.paymentRequired}</AlertTitle>
                    <AlertDescription>{profileCopy.paymentOverdue}</AlertDescription>
                </Alert>
            )}

            {isCancelingAtPeriodEnd ? (
                <Alert className="mb-6 border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-50">
                    <AlertCircle size={20} />
                    <AlertTitle>{profileCopy.subscriptionCanceling}</AlertTitle>
                    <AlertDescription data-testid="billing-canceling-notice">
                        {periodEndLabel
                            ? profileCopy.subscriptionCancelingOn(periodEndLabel)
                            : profileCopy.subscriptionCancelingPeriod}
                    </AlertDescription>
                </Alert>
            ) : null}

            {isCanceled ? (
                <Alert className="mb-6">
                    <AlertCircle size={20} />
                    <AlertTitle>{profileCopy.subscriptionCancelled}</AlertTitle>
                    <AlertDescription data-testid="billing-canceled-notice">
                        {profileCopy.subscriptionCancelledDescription}
                    </AlertDescription>
                </Alert>
            ) : null}

            {createPortal.error ? (
                <Alert className="mb-6">
                    <AlertCircle size={20} />
                    <AlertTitle>
                        {createPortal.variables?.flow === "cancel"
                            ? profileCopy.unableToCancel
                            : createPortal.variables?.flow === "update"
                              ? profileCopy.unableToChangePlan
                              : profileCopy.unableToOpenBillingPortal}
                    </AlertTitle>
                    <AlertDescription data-testid="billing-portal-error">{createPortal.error.message}</AlertDescription>
                </Alert>
            ) : null}

            {createCheckout.error ? (
                <Alert variant="destructive" className="mb-6">
                    <AlertCircle size={20} />
                    <AlertTitle>{profileCopy.unableToStartCheckout}</AlertTitle>
                    <AlertDescription>
                        {createCheckout.error instanceof Error
                            ? createCheckout.error.message
                            : profileCopy.pleaseTryAgain}
                    </AlertDescription>
                </Alert>
            ) : null}

            <div className="gap-6 grid">
                {/* Current Plan Card */}
                <Card>
                    <CardHeader>
                        <div className="flex justify-between items-start">
                            <div>
                                <CardTitle>{profileCopy.currentPlan}</CardTitle>
                                <CardDescription>
                                    {hasBilling ? profileCopy.currentPlanDescription : profileCopy.noActiveSubscription}
                                </CardDescription>
                            </div>
                            {billing?.subscription_plan && <SubscriptionPlanBadge plan={billing.subscription_plan} />}
                        </div>
                    </CardHeader>
                    <CardContent>
                        {hasBilling ? (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center py-2 border-b">
                                    <span className="font-medium text-slate-700 dark:text-slate-300 text-sm">
                                        {profileCopy.billingStatus}
                                    </span>
                                    <Badge
                                        variant={getStatusBadgeVariant(billing?.subscription_status)}
                                        data-testid="billing-status-badge"
                                    >
                                        {statusLabel}
                                    </Badge>
                                </div>
                                {(usageLoading || usageLabel) && (
                                    <div className="flex justify-between items-center gap-4 py-2 border-b">
                                        <span className="font-medium text-slate-700 dark:text-slate-300 text-sm">
                                            {profileCopy.diaryUsageUsedLabel}
                                        </span>
                                        {usageLoading ? (
                                            <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                                        ) : (
                                            <span className="text-slate-600 dark:text-slate-400 text-sm text-right">
                                                {usageLabel}
                                            </span>
                                        )}
                                    </div>
                                )}
                                {billing?.current_period_end && (
                                    <div className="flex justify-between items-center py-2 border-b">
                                        <span className="font-medium text-slate-700 dark:text-slate-300 text-sm">
                                            {isCancelingAtPeriodEnd || !isActive
                                                ? profileCopy.accessUntil
                                                : profileCopy.nextBillingDate}
                                        </span>
                                        <span className="text-slate-600 dark:text-slate-400 text-sm">
                                            {periodEndLabel}
                                        </span>
                                    </div>
                                )}
                                {billing?.subscription_plan === "trial" ? (
                                    <div className="flex justify-between items-center py-2">
                                        <span className="font-medium text-slate-700 dark:text-slate-300 text-sm">
                                            {profileCopy.planDetails}
                                        </span>
                                        <span className="text-slate-600 dark:text-slate-400 text-sm">
                                            {profileCopy.trialPlanDetails}
                                        </span>
                                    </div>
                                ) : null}
                                {billing?.subscription_plan === "enterprise" ? (
                                    <div className="flex justify-between items-center py-2">
                                        <span className="font-medium text-slate-700 dark:text-slate-300 text-sm">
                                            {profileCopy.planDetails}
                                        </span>
                                        <span className="text-slate-600 dark:text-slate-400 text-sm">
                                            {profileCopy.enterprisePlanDetails}
                                        </span>
                                    </div>
                                ) : null}
                                {atCap ? (
                                    <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 rounded-md text-amber-900 dark:text-amber-100 text-sm">
                                        <AlertCircle size={16} className="mt-0.5 shrink-0" />
                                        <p>{profileCopy.diaryUsageAtCapHint}</p>
                                    </div>
                                ) : null}
                            </div>
                        ) : (
                            <div className="py-8 text-center">
                                <CreditCard className="mx-auto mb-4 w-12 h-12 text-slate-400" />
                                <p className="mb-6 text-slate-600 dark:text-slate-400">
                                    {profileCopy.choosePlanToStart}
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>
                            {isTrialPlan
                                ? profileCopy.upgradeYourPlan
                                : hasBilling
                                  ? profileCopy.changeYourPlan
                                  : profileCopy.availablePlans}
                        </CardTitle>
                        <CardDescription>
                            {isTrialPlan
                                ? profileCopy.upgradePlanDescription
                                : hasBilling
                                  ? profileCopy.changePlanDescription
                                  : profileCopy.choosePlanDescription}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <PlanPickerCards
                            plan={billing?.subscription_plan}
                            isPending={isBusy}
                            onSubscribe={(plan) => createCheckout.mutate({ plan })}
                            onChangePlan={(plan) => {
                                if (hasStripeSubscription) {
                                    createPortal.mutate({ flow: "update" });
                                    return;
                                }
                                if (plan === "starter" && hasStripeCustomer) {
                                    createPortal.mutate();
                                    return;
                                }
                                createCheckout.mutate({ plan });
                            }}
                        />
                        <div className="space-y-3 mt-4 text-center">
                            {!hasBilling ? (
                                <p className="text-slate-600 dark:text-slate-400 text-sm">
                                    {profileCopy.preferStartFree}{" "}
                                    <a href="/signup/trial" className="font-medium text-foreground underline">
                                        {profileCopy.createFreeAccount}
                                    </a>
                                    .
                                </p>
                            ) : null}
                            <p className="text-slate-600 dark:text-slate-400 text-sm">
                                {profileCopy.needMore}{" "}
                                <a
                                    href={`${websiteUrl}/enterprise`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-cyan-600 hover:underline"
                                    data-testid="billing-contact-enterprise"
                                >
                                    {profileCopy.enterprisePlan}
                                </a>
                            </p>
                            {canCancelSubscription || hasStripeCustomer ? (
                                <div className="space-y-2 pt-1">
                                    <div className="flex flex-col sm:flex-row justify-center items-center gap-2">
                                        {canCancelSubscription ? (
                                            <Button
                                                onClick={() => createPortal.mutate({ flow: "cancel" })}
                                                disabled={createPortal.isPending}
                                                variant="ghost"
                                                className="text-destructive hover:text-destructive"
                                                data-testid="billing-cancel-subscription"
                                            >
                                                {createPortal.isPending ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    profileCopy.cancelSubscription
                                                )}
                                            </Button>
                                        ) : null}
                                        {hasStripeCustomer ? (
                                            <Button
                                                onClick={() => createPortal.mutate()}
                                                disabled={createPortal.isPending}
                                                variant="ghost"
                                                className="text-slate-600 dark:text-slate-400"
                                                data-testid="billing-manage-payment"
                                            >
                                                {profileCopy.managePaymentMethod}
                                            </Button>
                                        ) : null}
                                    </div>
                                    {isCancelingAtPeriodEnd ? (
                                        <p className="text-slate-600 dark:text-slate-400 text-sm">
                                            {profileCopy.undoCancellationHint}
                                        </p>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
